'use client';

import React from 'react';
import { Button } from '@/components/ui/button';
import { Check, ChevronDown } from 'lucide-react';
import { useUser } from '@/lib/context/user-context';
import { useUserSettings } from '@/contexts/user-settings-context';
import { useI18n } from '@/lib/i18n';

const CEFR_LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'] as const;
type CefrLevel = (typeof CEFR_LEVELS)[number] | '';

export const CefrLevelSelector: React.FC = () => {
  const { user } = useUser();
  const { userSettings, setUserSettings } = useUserSettings();
  const { t, lang } = useI18n() as any;
  const isChineseUI = lang === 'chinese';

  const titleCopy = t(
    'settings.cefrLevel.title',
    isChineseUI ? 'CEFR 等级' : 'CEFR level'
  );
  const descriptionCopy = t(
    'settings.cefrLevel.description',
    isChineseUI
      ? '选择最适合你的 CEFR 学习阶段，用于个性化内容推荐。'
      : 'Pick the CEFR stage that best matches your studies so we can personalize content.'
  );
  const unsetLabelCopy = t(
    'settings.cefrLevel.unset',
    isChineseUI ? '未设置' : 'Not set'
  );
  const savingCopy = t(
    'settings.cefrLevel.saving',
    isChineseUI ? '正在保存 CEFR 等级...' : 'Saving CEFR level...'
  );

  const [isOpen, setIsOpen] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const [selectedLevel, setSelectedLevel] = React.useState<CefrLevel>(
    (userSettings?.cefrLevel as CefrLevel) ?? ''
  );

  React.useEffect(() => {
    setSelectedLevel((userSettings?.cefrLevel as CefrLevel) ?? '');
  }, [userSettings?.cefrLevel]);

  const levelOptions = React.useMemo(
    () => [
      { value: '' as CefrLevel, label: unsetLabelCopy },
      ...CEFR_LEVELS.map((level) => ({
        value: level as CefrLevel,
        label: level,
      })),
    ],
    [unsetLabelCopy]
  );

  const currentOption =
    levelOptions.find((option) => option.value === selectedLevel) ??
    levelOptions[0];

  const handleSelect = async (level: CefrLevel) => {
    setIsOpen(false);
    const previousLevel = selectedLevel;
    setSelectedLevel(level);
    setUserSettings((prev) => ({
      ...prev,
      cefrLevel: level,
    }));

    if (!user?.id) {
      return;
    }

    try {
      setIsSaving(true);
      const response = await fetch('/api/user-settings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: user.id,
          settingKey: 'cefrLevel',
          value: level,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to update CEFR level: ${response.status}`);
      }
    } catch (error) {
      console.error('Error updating CEFR level:', error);
      setSelectedLevel(previousLevel);
      setUserSettings((prev) => ({
        ...prev,
        cefrLevel: previousLevel,
      }));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-lg sm:text-xl font-semibold text-foreground">
            {titleCopy}
          </h2>
          <p
            className={`${
              isChineseUI
                ? 'text-xs sm:text-sm font-normal'
                : 'text-sm sm:text-base'
            } text-muted-foreground`}
          >
            {descriptionCopy}
          </p>
        </div>
        <div className="relative">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsOpen((open) => !open)}
            disabled={isSaving}
            className="flex items-center gap-2 w-[140px] justify-between bg-background border-border hover:border-primary/30 hover:bg-muted/30 transition-all duration-200 rounded-lg"
            aria-label="Select CEFR level"
            aria-expanded={isOpen}
          >
            <span className="text-sm font-medium">
              {currentOption?.label ?? unsetLabelCopy}
            </span>
            <ChevronDown
              className={`h-3 w-3 text-muted-foreground transition-transform duration-200 ${
                isOpen ? 'rotate-180' : ''
              }`}
            />
          </Button>

          {isOpen && (
            <>
              <div
                className="fixed inset-0 z-40"
                onClick={() => setIsOpen(false)}
              />
              <div className="absolute top-full left-0 mt-2 w-[140px] bg-background border border-border rounded-lg shadow-xl z-50 overflow-hidden animate-in slide-in-from-top-2 duration-200">
                {levelOptions.map((option) => (
                  <button
                    key={option.value || 'unset'}
                    onClick={() => handleSelect(option.value)}
                    disabled={isSaving}
                    className={`w-full px-3 py-2.5 text-left flex items-center justify-between hover:bg-muted/50 transition-colors duration-150 ${
                      selectedLevel === option.value ? 'bg-primary/5' : ''
                    }`}
                    aria-label={
                      option.value
                        ? `Select CEFR ${option.label}`
                        : 'Clear CEFR level'
                    }
                  >
                    <span className="text-sm font-medium text-foreground">
                      {option.label}
                    </span>
                    {selectedLevel === option.value && (
                      <Check className="h-4 w-4 text-primary" />
                    )}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
      {isSaving && (
        <p className="text-xs text-muted-foreground">{savingCopy}</p>
      )}
    </div>
  );
};

export default CefrLevelSelector;
